import { ButtonBase, Chip, Stack, Typography, useTheme } from "@mui/material";
import { MoreHorizontal } from "lucide-react";
import { type FC, useState } from "react";
import { SolutionItemActionDrawer } from "web_myplann/Goals/Components/SolutionItemActionDrawer";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import {
	type GoalPortfolioResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	portfolio: GoalPortfolioResponseDTO;
	hasDivider: boolean;
	dueYear?: number;
	onUnpin: (portfolioId: number) => void;
	onEditAllocation?: (portfolio: GoalPortfolioResponseDTO) => void;
};

export const GoalDetailSolutionItem: FC<Props> = ({
	portfolio,
	hasDivider,
	dueYear,
	onUnpin,
	onEditAllocation,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const [actionOpen, setActionOpen] = useState(false);
	const closeAction = () => setActionOpen(false);

	const isSetAside = portfolio.type === GoalPortfolioTypeEnum.SetAside;
	const title = portfolio.domain
		? t(Resources.Portfolio.List.PortfolioDomainType[portfolio.domain])
		: "";
	const subtitle = [portfolio.producer, portfolio.product]
		.filter(Boolean)
		.join(" - ");
	const allocationPercent = Math.min(
		100,
		Math.max(0, portfolio.usagePercent ?? 0),
	);
	const showAllocation = !isSetAside && allocationPercent < 100;
	const displayValue = isSetAside
		? (portfolio.portfolioValue ?? 0)
		: (portfolio.usageValue ?? 0);
	const expectedCoverage = portfolio.expectedCoverage ?? 0;
	const showProjection =
		!isSetAside && expectedCoverage > 0 && dueYear !== undefined;

	return (
		<>
			<ButtonBase
				onClick={() => setActionOpen(true)}
				sx={{
					width: "100%",
					textAlign: "left",
					justifyContent: "flex-start",
					alignItems: "center",
					gap: 1.2,
					px: 1.6,
					py: 1.2,
					borderBottom: hasDivider
						? `1px solid ${theme.palette.grey[100]}`
						: "none",
				}}
			>
				<Stack flex={1} minWidth={0} gap={0.2}>
					<Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
						<Typography
							variant="bodyMedium"
							color={theme.palette.grey[900]}
							noWrap
						>
							{title}
						</Typography>
						{showAllocation && (
							<Chip
								variant="tag"
								label={t(Resources.Goals.Detail.Solution.allocationLabel, {
									percent: allocationPercent,
								})}
								sx={{ fontVariantNumeric: "tabular-nums" }}
							/>
						)}
					</Stack>
					{subtitle && (
						<Typography
							variant="caption"
							color={theme.palette.grey[500]}
							noWrap
						>
							{subtitle}
						</Typography>
					)}
				</Stack>
				<Stack alignItems="flex-end" flexShrink={0} gap={0.4}>
					<Typography
						variant="bodyStrong"
						color={theme.palette.grey[900]}
						tabular
					>
						{t(Resources.Goals.Detail.Solution.valueLabel, {
							amount: formatAmount(displayValue),
						})}
					</Typography>
					{showProjection && (
						<Typography
							variant="caption"
							color={theme.palette.primary.main}
							tabular
						>
							{t(Resources.Goals.Detail.Solution.expectedInYear, {
								amount: formatAmount(expectedCoverage),
								year: dueYear,
							})}
						</Typography>
					)}
				</Stack>
				<MoreHorizontal
					size={theme.iconSize.sm}
					color={theme.palette.grey[400]}
				/>
			</ButtonBase>
			<SolutionItemActionDrawer
				open={actionOpen}
				portfolio={portfolio}
				onClose={closeAction}
				onUnpin={onUnpin}
				onEditAllocation={onEditAllocation}
			/>
		</>
	);
};
